import React, { useEffect } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { MinusIcon, PlusIcon, XIcon } from 'lucide-react';
import { fitLabels } from '../types/product';
import { useCart } from '../contexts/CartContext';
import { formatINR } from '../utils/currency';
export function CartDrawer() {
  const {
    lines,
    isOpen,
    closeCart,
    setQuantity,
    removeLine,
    subtotal
  } = useCart();
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeCart();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [closeCart]);
  return <AnimatePresence data-id="element-51" data-mp="2qbvuvjdnj4gx">
      {isOpen && <>
          <motion.div key="scrim" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.35
      }} onClick={closeCart} className="fixed inset-0 z-50 bg-ink/25" aria-hidden="true" data-id="element-52" data-mp="2vayp08s3fqa4" />
          <motion.aside key="drawer" role="dialog" aria-modal="true" aria-label="Shopping bag" initial={{
        x: '100%'
      }} animate={{
        x: 0
      }} exit={{
        x: '100%'
      }} transition={{
        duration: 0.5,
        ease: [0.22, 1, 0.36, 1]
      }} className="fixed inset-y-0 right-0 z-50 flex w-full max-w-md flex-col bg-bone" data-id="element-53" data-mp="43hc2gwpie1q">
            <div className="flex items-center justify-between border-b border-greige/70 px-6 py-5" data-id="element-54" data-mp="mrmar9yib8to">
              <h2 className="eyebrow text-ink" data-id="element-55" data-mp="htkvl047btnh">Your bag</h2>
              <button type="button" onClick={closeCart} aria-label="Close bag" className="text-smoke transition-colors hover:text-ink" data-id="element-56" data-mp="vu5bnoiqfbda">
                <XIcon size={18} strokeWidth={1.25} data-id="element-57" data-mp="2xm9y4khgpggm" />
              </button>
            </div>

            {lines.length === 0 ? <div className="flex flex-1 flex-col items-center justify-center gap-5 px-8 text-center" data-id="element-58" data-mp="2tlq2xmgg8mx4">
                <p className="font-display text-2xl font-light text-ink" data-id="element-59" data-mp="wl3t2zfp3t2i">Your bag is empty</p>
                <p className="text-sm font-light text-smoke" data-id="element-60" data-mp="11k6khh2r60s0">
                  Six pieces await, each made in a small run.
                </p>
                <button type="button" onClick={closeCart} className="eyebrow border border-ink px-8 py-3.5 text-ink transition-colors hover:bg-ink hover:text-bone" data-id="element-61" data-mp="1bgt6waaq8zw5">
                  Browse the collection
                </button>
              </div> : <>
                <ul className="flex-1 divide-y divide-greige/70 overflow-y-auto px-6" data-id="element-62" data-mp="1p9rfwzg3eqi6">
                  {lines.map(line => <li key={`${line.product.id}-${line.size}-${line.fit}`} className="flex gap-4 py-6" data-id="element-63" data-mp="1z64adzjgp8dz">
                      <img src={line.product.images[line.fit]} alt="" className="h-28 shrink-0 object-cover" style={{
                width: '5.5rem'
              }} data-id="element-64" data-mp="235czgr3k4i37" />
                      <div className="flex flex-1 flex-col" data-id="element-65" data-mp="3epv3pgn8t2i1">
                        <div className="flex justify-between gap-3" data-id="element-66" data-mp="39ppbnzd29dzf">
                          <div data-id="element-67" data-mp="39rtm705gtb8o">
                            <p className="font-display text-lg font-light leading-tight text-ink" data-id="element-68" data-mp="b7tp8on9stiw">
                              {line.product.name}
                            </p>
                            <p className="mt-1 text-xs font-light leading-relaxed tracking-wide text-smoke" data-id="element-69" data-mp="6bctb89txav9">
                              {line.product.colour}
                              <br data-id="element-70" data-mp="3snrzwbxsb6w6" />
                              {fitLabels[line.fit]} · Size {line.size}
                            </p>
                          </div>
                          <span className="text-sm font-light text-ink" data-id="element-71" data-mp="p7cpkv9ub0kn">
                            {formatINR(line.product.price * line.quantity)}
                          </span>
                        </div>

                        <div className="mt-auto flex items-center justify-between pt-4" data-id="element-72" data-mp="dshoqiu0ddzz">
                          <div className="flex items-center border border-greige" data-id="element-73" data-mp="3vxuqg9ziyvf2">
                            <button type="button" aria-label="Decrease quantity" onClick={() => setQuantity(line.product.id, line.size, line.fit, line.quantity - 1)} className="px-2.5 py-1.5 text-smoke transition-colors hover:text-ink" data-id="element-74" data-mp="4qiafyadoo0q">
                              <MinusIcon size={13} strokeWidth={1.5} data-id="element-75" data-mp="1g6yrfw1kzcpp" />
                            </button>
                            <span className="min-w-[1.75rem] text-center text-xs text-ink" data-id="element-76" data-mp="30vaoo2pkrizf">
                              {line.quantity}
                            </span>
                            <button type="button" aria-label="Increase quantity" onClick={() => setQuantity(line.product.id, line.size, line.fit, line.quantity + 1)} className="px-2.5 py-1.5 text-smoke transition-colors hover:text-ink" data-id="element-77" data-mp="39p5sn7eyn3dh">
                              <PlusIcon size={13} strokeWidth={1.5} data-id="element-78" data-mp="66w22pqovt47" />
                            </button>
                          </div>
                          <button type="button" onClick={() => removeLine(line.product.id, line.size, line.fit)} className="text-[11px] uppercase tracking-[0.16em] text-smoke underline underline-offset-4 transition-colors hover:text-ink" data-id="element-79" data-mp="1ax6oi6y1aua">
                            Remove
                          </button>
                        </div>
                      </div>
                    </li>)}
                </ul>

                <div className="border-t border-greige/70 px-6 py-6" data-id="element-80" data-mp="2gbejrcn7oex9">
                  <div className="flex items-baseline justify-between" data-id="element-81" data-mp="2g9jtdit67uls">
                    <span className="eyebrow text-smoke" data-id="element-82" data-mp="2l9oe5l143bht">Subtotal</span>
                    <span className="font-display text-2xl font-light text-ink" data-id="element-83" data-mp="2q2r99xljet04">
                      {formatINR(subtotal)}
                    </span>
                  </div>
                  <p className="mt-2 text-xs font-light text-smoke" data-id="element-84" data-mp="1xl534xqogkm1">
                    Complimentary shipping nationwide. Prices are fixed — we never discount.
                  </p>
                  <button type="button" className="eyebrow mt-5 w-full border border-ink bg-ink py-4 text-bone transition-colors hover:bg-transparent hover:text-ink" data-id="element-85" data-mp="1l2t0ehbk044u">
                    Proceed to checkout
                  </button>
                </div>
              </>}
          </motion.aside>
        </>}
    </AnimatePresence>;
}